import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { BusinessName } from "@/components/storefront/common";
import { apiError } from "@/lib/api";
import { Loader2, ArrowLeft } from "lucide-react";

export function AuthShell({ title, subtitle, children }) {
  const { business, settingsLoading } = useStore();
  const name = business?.name || "";
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12">
        <Link to="/" className="flex items-center gap-2">
          <span className="h-9 w-9 rounded-lg bg-amber-500 text-slate-900 grid place-items-center font-display font-bold">S</span>
          <span className="font-display font-bold text-lg"><BusinessName name={name} loading={settingsLoading} /></span>
        </Link>
        <div>
          <h2 className="text-4xl font-bold font-display leading-tight">Genuine gadgets,<br />delivered with trust.</h2>
          <p className="text-slate-400 mt-4 max-w-sm">Shop authentic accessories, track your orders and manage everything in one place.</p>
        </div>
        <p className="text-xs text-slate-500">© {new Date().getFullYear()}{name ? ` ${name}` : ""}</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden inline-flex items-center gap-1.5 text-sm text-slate-500 mb-6"><ArrowLeft className="h-4 w-4" /> Back to store</Link>
          <h1 className="text-2xl font-bold text-slate-900 font-display">{title}</h1>
          <p className="text-slate-500 mt-1 text-sm">{subtitle}</p>
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(form.email, form.password);
      navigate(location.state?.from || "/account");
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to your account">
      <form onSubmit={submit} className="space-y-4">
        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3" data-testid="login-error">{error}</p>}
        <Field label="Email" type="email" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} testid="login-email" />
        <Field label="Password" type="password" value={form.password} onChange={(v) => setForm((f) => ({ ...f, password: v }))} testid="login-password" />
        <div className="text-right"><Link to="/forgot-password" className="text-sm text-amber-600 font-medium">Forgot password?</Link></div>
        <button disabled={loading} className="w-full inline-flex justify-center items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 rounded-lg transition disabled:opacity-60" data-testid="login-submit">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Log In
        </button>
        <p className="text-sm text-center text-slate-500">No account? <Link to="/register" className="text-amber-600 font-semibold">Create one</Link></p>
      </form>
    </AuthShell>
  );
}

export function Field({ label, type = "text", value, onChange, testid, autoComplete, name }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700">{label}</label>
      <input type={type} required value={value} name={name} autoComplete={autoComplete} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full h-11 px-3 mt-1 rounded-lg border border-slate-200 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 text-sm transition" />
    </div>
  );
}
